import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { portfolioService } from '../services/portfolio';
import { Loader2, X } from 'lucide-react';
import { getApiOrigin } from '../lib/api-url';
import { categoriesService } from '../services/categories';
import type { PortfolioMediaTile } from '../lib/portfolio-tiles';
import { getMediaAlt } from '../lib/portfolio-tiles';
import PortfolioNetflixGallery from '../components/portfolio/PortfolioNetflixGallery';

const imgSrc = (url?: string) => (!url ? '' : url.startsWith('http') ? url : `${getApiOrigin()}${url}`);

export function PortfolioPage() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<Awaited<ReturnType<typeof portfolioService.getAll>>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(null);

  const selectedCategory = searchParams.get('category')?.trim() || 'all';

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([portfolioService.getAll(), categoriesService.getAll()])
      .then(([portfolioData, cats]) => {
        setItems(portfolioData.filter((i) => i.isActive));
        setCategories(cats);
      })
      .catch((err) => {
        console.error(err);
        setLoadError('Impossible de charger la galerie. Réessayez dans quelques instants.');
      })
      .finally(() => setLoading(false));
  }, []);

  const openTile = (tile: PortfolioMediaTile) => {
    if (tile.kind === 'video' && tile.videoUrl) {
      window.open(tile.videoUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setLightbox({ url: tile.imageUrl, alt: getMediaAlt(tile) });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-950">
        <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
        <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
          Chargement de la galerie...
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-950 px-6 text-center">
        <p className="max-w-md font-medium text-zinc-400">{loadError}</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-zinc-950 text-white select-none"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
    >
      <PortfolioNetflixGallery
        items={items}
        categories={categories}
        imgSrc={imgSrc}
        onOpenTile={openTile}
        filterCategoryId={selectedCategory === 'all' ? null : selectedCategory}
        showHero={selectedCategory === 'all'}
      />

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
            aria-label="Fermer"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={imgSrc(lightbox.url)}
            alt={lightbox.alt}
            draggable={false}
            className="max-h-[90vh] max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )}
    </div>
  );
}
